@if (transaction()) {
  <!--
    SUMMARY (gs-portal-redesign) — restyled per Figma node 1688-41 ("Order
    Summary" card): centered black title + bold centered subtitle,
    `justify-between` rows (label left, colored value pill right). Row
    colors follow the pill's meaning rather than one shared palette: brand
    blue (filled) for the Group Seats subtotal, mustard (filled) for an
    active upsell, green (filled) for an applied discount, and a neutral
    bordered pill for the plain fee/tax/rain-out rows. All value pills
    share the same fixed 84×28 geometry (`h-7 min-w-[84px]`) regardless of
    color, so they line up in one column down the right edge of the card.
    Same bindings/copy/data-cy hooks as before — style/layout only.

    "Blue" above is the club's own brand colour, not a fixed hex: the seat
    subtotal / Total Balance pills use `text-main` (`rgb(var(--default-color))`)
    so each tenant renders in its own theme colour instead of every club
    sharing Figma's flat `#2d8ad9` placeholder (plan Decision #3).
  -->
  <div
    id="cart-summary"
    class="bg-white shadow-[0px_3px_6px_0px_rgba(0,0,0,0.16)] py-6 rounded-3xl">
    <div class="px-8 pb-4 text-center">
      <h5 class="text-2xl font-extrabold text-black">Order Summary</h5>
      @if (summaryText()) {
        <small class="block mt-1 text-sm font-bold text-black">{{
          summaryText()
        }}</small>
      }
    </div>

    <div class="flex flex-col gap-3 px-8 py-5">
      @for (seat of seats; track $index) {
        <div class="flex justify-between items-center gap-3">
          <h5 class="text-xs font-bold text-main">
            Group Seats
            <span class="font-medium text-[#404041]">
              (${{ seat.price | number: '1.0' }} x {{ seat.quantity }} seats)
            </span>
          </h5>
          <h5
            data-cy="summary-seats-price"
            class="flex h-7 min-w-[84px] flex-shrink-0 items-center justify-center rounded-[10px] bg-[#dfedf9] px-3 text-center text-xs font-bold text-main">
            ${{
              seats.length === 1
                ? (totalPriceSeats || 0 | number: '1.2-2')
                : (seat.total || totalPriceSeats || 0 | number: '1.2-2')
            }}
          </h5>
        </div>
      }

      @if (
        hasActiveDiscount && totalDiscount !== null && isDiscountCodesEnabled()
      ) {
        <div
          data-cy="summary-discount"
          class="flex justify-between items-center gap-3">
          <h5 class="text-xs font-medium text-[#676767]">
            Discount ({{ discountCode }}):
          </h5>
          <h5
            class="flex h-7 min-w-[84px] flex-shrink-0 items-center justify-center rounded-[10px] bg-[#4c8e42]/10 px-3 text-center text-xs font-bold text-[#4c8e42]">
            − ${{ totalDiscount | number: '1.2-2' }}
          </h5>
        </div>
      }

      @if (upsellSelected()) {
        <div class="flex justify-between items-center gap-3">
          <h5 class="text-xs font-bold text-[#e2ac30]">
            Upgrade added:
            <span class="font-normal text-black">
              {{ upsellSelected()?.name }}
              (${{ upsellSelected()?.price | number: '1.0' }} x
              {{ totalTransactionSeats() }}):
            </span>
          </h5>
          <h5
            data-cy="summary-upsell-item"
            class="flex h-7 min-w-[84px] flex-shrink-0 items-center justify-center rounded-[10px] bg-[#f3c760]/20 px-3 text-center text-xs font-bold text-[#e2ac30]">
            + ${{
              (upsellSelected()?.price || 0) * totalTransactionSeats()
                | number: '1.2-2'
            }}
          </h5>
        </div>
      }

      @if (
        rainoutValue() &&
        transaction().transaction.tdcTransaction?.insuranceRainoutPrice
      ) {
        <div class="flex justify-between items-center gap-3">
          <h5 class="text-xs font-medium text-[#676767]">
            Rain-out Insurance:
          </h5>
          <h5
            data-cy="summary-insurance-rainout-price"
            class="flex h-7 min-w-[84px] flex-shrink-0 items-center justify-center rounded-[10px] border border-[#eee] px-3 text-center text-xs font-bold text-[#404041]">
            ${{
              transaction().transaction.tdcTransaction?.insuranceRainoutPrice ||
                0 | number: '1.2-2'
            }}
          </h5>
        </div>
      }
      @if (isTicketFeeEnabled()) {
        @for (serviceCharge of serviceChargesDetails; track $index) {
          @if (serviceCharge.amount) {
            <div class="flex justify-between items-center gap-3">
              <h5 class="text-xs font-medium text-[#676767]">
                {{ serviceCharge.name }}:
              </h5>
              <h5
                data-cy="summary-service-charge-fee"
                class="flex h-7 min-w-[84px] flex-shrink-0 items-center justify-center rounded-[10px] border border-[#eee] px-3 text-center text-xs font-bold text-[#404041]">
                ${{ serviceCharge.amount | number: '1.2-2' }}
              </h5>
            </div>
          }
        }
      } @else {
        @if (transaction().transaction.tdcTransaction?.serviceCharges) {
          <div class="flex justify-between items-center gap-3">
            <h5 class="text-xs font-medium text-[#676767]">
              Total ticket fees:
            </h5>
            <h5
              data-cy="summary-service-charge-fee"
              class="flex h-7 min-w-[84px] flex-shrink-0 items-center justify-center rounded-[10px] border border-[#eee] px-3 text-center text-xs font-bold text-[#404041]">
              ${{
                actualServiceChargesAmountWithoutInsuranceAndUpsell
                  | number: '1.2-2'
              }}
            </h5>
          </div>
        }
      }
    </div>

    @if (acquiredUpgrades.length) {
      <div class="flex flex-col gap-3 px-8 py-5 border-gray-100 border-b">
        @for (upgrade of acquiredUpgrades; track upgrade.id) {
          <div class="flex justify-between items-center gap-3">
            <h5 class="text-[#535353]">
              <span class="font-normal text-[#8B8B8B]"
                >For the purchase of
                <span class="font-bold text-[#535353]"
                  >{{ upgrade.goal }} Tickets</span
                >
                <br />
                we have included a
                <span class="font-bold text-[#535353]">{{ upgrade.name }}</span>
              </span>
            </h5>
            <h5
              class="flex-shrink-0 bg-[#E2BA36]/15 px-3 py-2 rounded-lg font-bold text-[#8B8B8B] text-center">
              Free Gift
            </h5>
          </div>
        }
      </div>
    }

    <div class="px-8 pt-5">
      <div
        class="flex justify-center items-center gap-1 rounded-2xl bg-[#f2f6fa] px-5 py-3">
        <h5 class="text-base font-extrabold text-main">Total Balance:</h5>
        <h5
          data-cy="summary-total-balance"
          class="text-base font-extrabold text-main">
          @if (transaction().transaction.tdcTransaction?.balance) {
            ${{
              transaction().transaction.tdcTransaction?.balance
                | number: '1.2-2'
            }}
          }
        </h5>
      </div>
    </div>
  </div>
}
